import React, { useState } from 'react';
import './StaffSection.css';

interface StaffMember {
  id: number;
  name: string;
  position: string;
  email: string;
  phone: string;
  bio: string;
  image?: string;
}

const StaffSection: React.FC = () => {
  const [selectedMember, setSelectedMember] = useState<StaffMember | null>(null);

  const staffMembers: StaffMember[] = [
    {
      id: 1,
      name: 'Kamuli Zila',
      position: 'Developer',
      email: 'kamulizila97@gmail.com',
      phone: '+255 768 494 444',
      bio: 'Kamuli Zila founded ZiTech Solutions with a vision to empower organizations through innovative digital solutions. With expertise in full-stack development and a passion for technology, he leads the company\'s technical strategy and innovation.',
    },
    {
      id: 2,
      name: 'Rebeca Samanda',
      position: 'Developer',
      email: 'rebecasamanda99@gmail.com',
      phone: '+255 763 241 307',
      bio: 'Rebeca Samanda leads our technology strategy and innovation. With a background in AI and machine learning, she ensures our solutions are cutting-edge and scalable, delivering exceptional value to our clients.',
    },
  ];

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // SVG Icons
  const Icons = {
    Email: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    Phone: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
      </svg>
    ),
    ArrowRight: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    ),
    ArrowLeft: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
    ),
    User: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  };

  // If a member is selected, show the detail page
  if (selectedMember) {
    return (
      <section id="staff" className="staff-section">
        <div className="staff-container">
          <button 
            className="staff-back-btn"
            onClick={() => setSelectedMember(null)}
          >
            <Icons.ArrowLeft />
            Back to Team
          </button>

          <div className="staff-detail">
            <div className="staff-detail-header">
              <div className="staff-detail-avatar">
                {getInitials(selectedMember.name)}
              </div>
              <h2 className="staff-detail-name">{selectedMember.name}</h2>
              <p className="staff-detail-position">{selectedMember.position}</p>
              <div className="staff-detail-divider"></div>
            </div>
            <div className="staff-detail-body">
              <div className="staff-detail-bio">
                <p>{selectedMember.bio}</p>
              </div>
              <div className="staff-detail-contact">
                <div className="staff-detail-contact-item">
                  <span className="staff-detail-contact-icon">
                    <Icons.Email />
                  </span>
                  <span className="staff-detail-contact-text">{selectedMember.email}</span>
                </div>
                <div className="staff-detail-contact-item">
                  <span className="staff-detail-contact-icon">
                    <Icons.Phone />
                  </span>
                  <span className="staff-detail-contact-text">{selectedMember.phone}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="staff" className="staff-section">
      <div className="staff-container">
        <div className="staff-header">
          <div className="staff-header-badge">Our Team</div>
          <h2 className="staff-title">Meet Our Team</h2>
          <p className="staff-subtitle">
            Our talented team of experts is dedicated to delivering excellence
          </p>
        </div>

        <div className="staff-table-wrapper">
          <table className="staff-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Name</th>
                <th>Position</th>
                <th>Email</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {staffMembers.map((member) => (
                <tr key={member.id}>
                  <td>
                    <div className="staff-table-avatar">
                      {getInitials(member.name)}
                    </div>
                  </td>
                  <td>
                    <span className="staff-table-name">{member.name}</span>
                  </td>
                  <td>
                    <span className="staff-table-position">{member.position}</span>
                  </td>
                  <td>
                    <span className="staff-table-email">{member.email}</span>
                  </td>
                  <td>
                    <button 
                      className="staff-view-btn"
                      onClick={() => setSelectedMember(member)}
                    >
                      View Profile
                      <Icons.ArrowRight />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default StaffSection;